/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { ResizeSensor } from '@blueprintjs/core';
import React, { useContext, useEffect, useState } from 'react';
import { useFetchEndpointData } from 'hooks/useFetchEndpointData';
import { WidgetContainer } from '/views/common/widgetContainer';
import { ErrorBoundary } from 'components/errorBoundary';
import { ColorScalesContext } from 'views/common/colorScalesContext';
import { useDefaultColors } from 'utilities/setUpColorScale';

/**
 * A ScreenCard provides a generalized method for rendering a chart onto a
 * Screen. The Card is responsible for rendering a ChartContainer,
 * which in turn will instantiate and render the specific Chart.
 *
 * @param {string} widgetType
 * @param {object} endpointQuery
 * @param {object} widgetConfiguration
 * @param selectedDefinitionName
 * @param activeResult
 * @returns {*}
 * @constructor
 */
export const ScreenCard = ({
	widgetType,
	endpointQuery,
	widgetConfiguration,
	selectedDefinitionName,
	activeResult,
}) => {
	// Store dimensions of things
	const [dimensions, setDimensions] = useState({ width: 300, height: 400 });

	// resize the widgets appropriately
	function onResize(entries) {
		const entry = entries[0].contentRect;
		setDimensions({ width: entry.width, height: entry.height });
	}

	const appSpecificColors = useContext(ColorScalesContext);
	const getColorScale = useDefaultColors(appSpecificColors);

	return (
		<ColorScalesContext.Provider value={getColorScale}>
			<div css={cardCss}>
				<ErrorBoundary
					description={
						'An error occurred while fetching data for this widget. Adjust the parameter and try again or refresh the page.'
					}
					retryButtonText={'Fetch again'}
				>
					<ResizeSensor onResize={onResize}>
						<div css={containerCss}>
							<ExplorerData
								dimensions={dimensions}
								type={widgetType}
								endpointQuery={endpointQuery}
								widgetConfiguration={widgetConfiguration}
								result={activeResult}
								selectedDefinitionName={selectedDefinitionName}
							/>
						</div>
					</ResizeSensor>
				</ErrorBoundary>
			</div>
		</ColorScalesContext.Provider>
	);
};

function ExplorerData({ dimensions, type, result, selectedDefinitionName, endpointQuery, widgetConfiguration }) {
	// fetch the filtered data
	const { data, loading } = useFetchEndpointData(result, selectedDefinitionName, type, endpointQuery);

	// Choose when data refreshes
	const [validData, setValidData] = useState(null);

	useEffect(() => {
		if (data || result?.resultId === 0) {
			setValidData(data);
		}
	}, [data, result]);

	return (
		<WidgetContainer
			data={validData}
			type={type}
			loading={loading}
			dimensions={dimensions}
			widgetConfiguration={widgetConfiguration}
			endpointQuery={endpointQuery}
			selectedDefinitionName={selectedDefinitionName}
		/>
	);
}

const cardCss = css`
	flex: 1 1 auto;
	display: flex;
	flex-flow: column;
	overflow: hidden;
`;

const containerCss = css`
	flex: 1 1 auto;
	overflow: hidden;
	position: relative;
`;
